import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 * @property {HTMLTemplateElement} [mediaDiscountBadgeTemplate]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.syncMediaDiscountBadge();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    /** @type {HTMLElement | null} */
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    const { mediaDiscountBadgeTemplate } = this.refs;
    /** @type {HTMLTemplateElement | null} */
    const newMediaDiscountBadgeTemplate = newProductPrice.querySelector('[ref="mediaDiscountBadgeTemplate"]');

    if (newMediaDiscountBadgeTemplate && mediaDiscountBadgeTemplate) {
      mediaDiscountBadgeTemplate.replaceWith(newMediaDiscountBadgeTemplate);
    } else if (newMediaDiscountBadgeTemplate && !mediaDiscountBadgeTemplate) {
      this.appendChild(newMediaDiscountBadgeTemplate);
    } else if (!newMediaDiscountBadgeTemplate && mediaDiscountBadgeTemplate) {
      mediaDiscountBadgeTemplate.remove();
    }

    if (newProductPrice.dataset.showDiscountOnMedia !== undefined) {
      this.dataset.showDiscountOnMedia = newProductPrice.dataset.showDiscountOnMedia;
    }

    this.syncMediaDiscountBadge();
  };

  /**
   * Mirrors the PDP discount badge onto the first product image when enabled.
   */
  syncMediaDiscountBadge() {
    const showOnMedia = this.dataset.showDiscountOnMedia === 'true';
    /** @type {HTMLElement | null} */
    const anchor = this.closest('.shopify-section, dialog')?.querySelector('[data-pdp-media-discount-badge-anchor]') || null;

    if (!anchor) return;

    if (!showOnMedia) {
      anchor.hidden = true;
      anchor.replaceChildren();
      return;
    }

    const { mediaDiscountBadgeTemplate } = this.refs;
    const badgeFromTemplate = mediaDiscountBadgeTemplate?.content?.firstElementChild?.cloneNode(true);
    const badgeFromPrice = this.querySelector('[ref="priceContainer"] .price-discount-badge')?.cloneNode(true);
    const badge = badgeFromTemplate || badgeFromPrice;

    if (!badge) {
      anchor.hidden = true;
      anchor.replaceChildren();
      return;
    }

    anchor.replaceChildren(badge);
    anchor.hidden = false;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
